"use client";

import Link from "@/components/ui/intent-link";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Download } from "lucide-react";

import { AssetImage } from "@/components/optilase/asset-image";
import { RegionFlag } from "@/components/optilase/region-flag";
import { SOCIAL_ICONS, SOCIAL_LABEL } from "@/components/layout/social-icons";
import { BRAND } from "@/lib/brand";
import type { SocialPlatform } from "@/lib/nav";
import { regionPath } from "@/lib/region";
import { useRegionSwitchHrefs } from "@/lib/region-paths-context";
import { settleRegionChoice } from "@/lib/region-switch";
import { titleCase } from "@/lib/title-case";
import { cn } from "@/lib/utils";
import { usePublicPathname } from "@/lib/public-pathname";

export type FooterLink = { label: string; href: string; download?: boolean };
export type FooterGroup = { heading: string; headingHref?: string; links: FooterLink[] };
export type FooterColumn = { groups: FooterGroup[] };
export type SocialLink = { platform: SocialPlatform; href: string };

export type FooterViewProps = {
  columns: FooterColumn[];
  email: string;
  phone: string;
  socials: SocialLink[];
  legalLinks: FooterLink[];
  copyright: string;
  region?: "ie" | "uk";
  regionLabel?: string;
};

function useIsActive() {
  const pathname = usePublicPathname();
  const normalise = (path: string) => path.replace(/^\/uk(?=\/|$)/, "").replace(/\/+$/, "") || "/";
  const current = normalise(pathname);
  return (href: string) => current === normalise(href.split(/[?#]/)[0]);
}

function FooterNavLink({ link, active }: { link: FooterLink; active: boolean }) {
  if (link.download) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm leading-5 text-white hover:underline"
      >
        <Download className="size-4 shrink-0" strokeWidth={2.25} aria-hidden />
        {titleCase(link.label)}
      </a>
    );
  }
  return (
    <Link
      href={link.href}
      aria-current={active ? "page" : undefined}
      className={cn("text-sm leading-5 hover:underline", active ? "text-teal-04" : "text-white")}
    >
      {titleCase(link.label)}
    </Link>
  );
}

function AccordionChevron({ open }: { open: boolean }) {
  return (
    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-white/10">
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn("origin-center transition-transform", open && "rotate-180")}
        />
      </svg>
    </span>
  );
}

export default function FooterView({
  columns,
  email,
  phone,
  socials,
  legalLinks,
  copyright,
  region = "ie",
  regionLabel = "Ireland",
}: FooterViewProps) {
  const isActive = useIsActive();
  const [open, setOpen] = useState<string | null>(null);
  const groups = columns.flatMap((c) => c.groups);

  return (
    <footer className="bg-navy-03 border-t border-white/10 text-white">
      <div className="mx-auto max-w-360 px-5 py-10 md:px-10 lg:px-20">
        <div className="flex flex-col gap-10 lg:flex-row lg:justify-between lg:gap-0">
          <div className="flex w-full flex-col gap-4 lg:w-[308px] lg:shrink-0">
            <Link
              href={regionPath("/", region)}
              aria-label={`${BRAND.name} — home`}
              className="pb-5"
            >
              <AssetImage
                src="/assets/logo/optilase/logo-white.svg"
                alt={BRAND.logoAlt}
                width={125}
                height={45}
                className="h-9 w-auto lg:h-11.25"
              />
            </Link>

            <div className="flex flex-col gap-4">
              <a
                href={`mailto:${email}`}
                className="text-lg leading-5 font-semibold hover:underline"
              >
                {email}
              </a>
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="text-lg leading-5 font-semibold hover:underline"
              >
                {phone}
              </a>

              <ul className="flex items-center gap-2">
                {socials.map((s) => (
                  <li key={s.platform}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={SOCIAL_LABEL[s.platform]}
                      className="grid size-9 place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                    >
                      {SOCIAL_ICONS[s.platform]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-col md:hidden">
            {groups.map((g) => {
              const isOpen = open === g.heading;
              return (
                <div key={g.heading} className="border-t border-white/15 first:border-t-0">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : g.heading)}
                    aria-expanded={isOpen}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 py-4 text-left"
                  >
                    <span className="text-h6">{titleCase(g.heading)}</span>
                    <AccordionChevron open={isOpen} />
                  </button>
                  {isOpen ? (
                    <ul className="flex flex-col gap-2 pb-4">
                      {g.links.map((l) => (
                        <li key={l.label}>
                          <FooterNavLink link={l} active={isActive(l.href)} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="hidden w-full md:grid md:grid-cols-4 md:gap-4 lg:flex lg:min-w-0 lg:flex-1 lg:gap-[46.67px]">
            {columns.map((col, ci) => (
              <div key={col.groups[0]?.heading ?? ci} className="lg:min-w-0 lg:flex-1">
                {col.groups.map((g, gi) => (
                  <div key={g.heading} className={gi > 0 ? "pt-5" : undefined}>
                    <h2 className="text-teal-05 text-xl font-semibold">
                      {g.headingHref ? (
                        <Link href={g.headingHref} className="underline-offset-4 hover:underline">
                          {titleCase(g.heading)}
                        </Link>
                      ) : (
                        titleCase(g.heading)
                      )}
                    </h2>
                    <ul className="mt-4 flex flex-col gap-2">
                      {g.links.map((l) => (
                        <li key={l.label}>
                          <FooterNavLink link={l} active={isActive(l.href)} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {ci === columns.length - 1 ? (
                  <div className="pt-5">
                    <RegionRow region={region} label={regionLabel} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div
          data-footer-legal
          className="border-navy-02 mt-10 flex flex-col gap-4 border-t pt-5 md:pt-[21px] lg:mt-14 lg:flex-row lg:items-center lg:justify-between"
        >
          <div className="flex flex-col gap-2 text-xs leading-5 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-2">
            <p className="lg:after:ml-2 lg:after:content-['·']">{copyright}</p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {legalLinks.map((l, i) => (
                <span key={l.label} className="flex items-center gap-2">
                  {i > 0 ? <span className="text-navy-01">|</span> : null}
                  <Link href={l.href} className="hover:underline">
                    {titleCase(l.label)}
                  </Link>
                </span>
              ))}
            </p>
          </div>

          <div className="flex flex-col gap-4 lg:shrink-0 lg:flex-row lg:items-center lg:gap-2.5">
            <div className="flex items-center gap-2.5">
              <span className="text-xs leading-5 whitespace-nowrap">
                Flexible payment plans with
              </span>
              <AssetImage
                src="/assets/optilase/icons/humm.svg"
                alt="humm"
                width={62}
                height={12}
                className="h-3 w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

const REGIONS: { value: "ie" | "uk"; name: string }[] = [
  { value: "ie", name: "Ireland" },
  { value: "uk", name: "United Kingdom" },
];

function RegionRow({ region, label }: { region: "ie" | "uk"; label: string }) {
  const pathname = usePublicPathname();
  const hrefs = useRegionSwitchHrefs();
  const live: "ie" | "uk" = pathname === "/uk" || pathname.startsWith("/uk/") ? "uk" : region;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const focusItem = (i: number) => {
    const items = itemRefs.current.filter(Boolean) as HTMLAnchorElement[];
    items[(i + items.length) % items.length]?.focus();
  };

  const onTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      flushSync(() => setOpen(true));
      focusItem(0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      flushSync(() => setOpen(true));
      focusItem(-1);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const items = itemRefs.current.filter(Boolean) as HTMLAnchorElement[];
    const i = items.indexOf(e.target as HTMLAnchorElement);
    focusItem(e.key === "ArrowDown" ? i + 1 : i - 1);
  };

  const current = REGIONS.find((r) => r.value === live) ?? REGIONS[0];

  return (
    <div
      ref={ref}
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <h2 className="text-teal-05 text-sm font-semibold">Region</h2>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        aria-haspopup="menu"
        aria-expanded={open}
        className="hover:text-teal-04 mt-2 flex w-fit cursor-pointer items-center gap-2 rounded-full text-sm transition-colors"
      >
        <RegionFlag region={current.value} size={16} />
        <span>{live === region ? label || current.name : current.name}</span>
        <svg
          width="9"
          height="5"
          viewBox="0 0 9 5"
          aria-hidden
          className={cn("transition-transform", open && "rotate-180")}
        >
          <path
            d="M1 1l3.5 3L8 1"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          onKeyDown={onMenuKey}
          className="text-grey-09 absolute bottom-full left-0 z-10 mb-2 w-56 rounded-xl bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] ring-1 ring-black/5"
        >
          {REGIONS.map((r, i) => (
            <a
              key={r.value}
              href={hrefs[r.value]}
              onClick={() => {
                settleRegionChoice();
                setOpen(false);
              }}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              role="menuitem"
              tabIndex={-1}
              className={cn(
                "hover:bg-teal-01 flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
                r.value === live ? "bg-teal-01 font-semibold" : "text-grey-07",
              )}
            >
              <RegionFlag region={r.value} size={16} />
              <span className="flex-1 text-left">{r.name}</span>
              {r.value === live ? (
                <svg width="14" height="11" viewBox="0 0 14 11" aria-hidden>
                  <path
                    d="M1 5.5L5 9.5L13 1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-teal-07"
                  />
                </svg>
              ) : null}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
